import { useState, useEffect, useCallback, useMemo } from 'react';
import { useApi } from '../../hooks/useApi';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import { MessageSquarePlus, Link2, BarChart3, Trash2, Plus, X, Loader2, Star } from 'lucide-react';
import { TraineeFilterBar, useTraineeFilter, ALL as SCOPE_ALL } from './TraineeFilterContext';

const ALL = '__all__';
const QTYPES = [
  { value: 'rating', label: 'Rating (1–5 ★)' },
  { value: 'nps', label: 'NPS (0–10)' },
  { value: 'choice', label: 'Single choice' },
  { value: 'text', label: 'Free text' },
];
const uid = () => Math.random().toString(36).slice(2, 9);

export default function FeedbackTab() {
  const { get, post, del } = useApi();
  const { batchId: scopeBatchId, departmentId, visibleBatches, batches } = useTraineeFilter();
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [builderOpen, setBuilderOpen] = useState(false);
  const [results, setResults] = useState(null);

  // builder state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [batchId, setBatchId] = useState(ALL);
  const [anonymous, setAnonymous] = useState(true);
  const [questions, setQuestions] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await get('/feedback/forms'); setForms(r.forms || []); }
    catch { toast.error('Failed to load forms'); }
    finally { setLoading(false); }
  }, [get]);
  useEffect(() => { load(); }, [load]);

  // Cascading Department → Batch filter applied to the forms list.
  const visibleForms = useMemo(() => {
    if (scopeBatchId !== SCOPE_ALL) return forms.filter(f => f.batch_id === scopeBatchId);
    if (departmentId !== SCOPE_ALL) {
      const ids = new Set(visibleBatches.map(b => b.batch_id));
      return forms.filter(f => ids.has(f.batch_id));
    }
    return forms;
  }, [forms, scopeBatchId, departmentId, visibleBatches]);

  const resetBuilder = () => {
    setTitle(''); setDescription(''); setBatchId(ALL); setAnonymous(true);
    setQuestions([{ id: uid(), type: 'rating', prompt: '', options: [], required: true }]);
  };

  const openBuilder = () => { resetBuilder(); setBuilderOpen(true); };

  const addQ = (type) => setQuestions(q => [...q, { id: uid(), type, prompt: '', options: type === 'choice' ? ['', ''] : [], required: false }]);
  const updateQ = (id, patch) => setQuestions(q => q.map(x => x.id === id ? { ...x, ...patch } : x));
  const removeQ = (id) => setQuestions(q => q.filter(x => x.id !== id));

  const save = async () => {
    if (!title.trim()) { toast.error('Title is required'); return; }
    const clean = questions
      .filter(q => q.prompt.trim())
      .map(q => ({
        id: q.id, type: q.type, prompt: q.prompt.trim(), required: !!q.required,
        options: q.type === 'choice' ? q.options.map(o => o.trim()).filter(Boolean) : [],
      }));
    if (clean.length === 0) { toast.error('Add at least one question with a prompt'); return; }
    if (clean.some(q => q.type === 'choice' && q.options.length < 2)) {
      toast.error('Choice questions need at least 2 options'); return;
    }
    setSaving(true);
    try {
      await post('/feedback/forms', {
        title: title.trim(), description: description.trim() || null,
        batch_id: batchId === ALL ? null : batchId, anonymous, questions: clean,
      });
      toast.success('Feedback form created');
      setBuilderOpen(false);
      await load();
    } catch { toast.error('Save failed'); } finally { setSaving(false); }
  };

  const copyLink = (form) => {
    const url = `${window.location.origin}/feedback/${form.ref_code}`;
    navigator.clipboard?.writeText(url).then(
      () => toast.success('Public link copied'),
      () => toast.message(url),
    );
  };

  const remove = async (form) => {
    if (!window.confirm(`Delete "${form.title}" and all its responses?`)) return;
    try { await del(`/feedback/forms/${form.form_id}`); toast.success('Deleted'); await load(); }
    catch { toast.error('Delete failed'); }
  };

  const openResults = async (form) => {
    try { const r = await get(`/feedback/forms/${form.form_id}/results`); setResults(r); }
    catch { toast.error('Failed to load results'); }
  };

  return (
    <div className="space-y-5" data-testid="feedback-tab">
      <div className="flex justify-between items-end gap-3 flex-wrap">
        <div className="space-y-2">
          <p className="text-sm text-muted-foreground">Build feedback forms and share a public link — trainees respond without signing in.</p>
          <TraineeFilterBar />
        </div>
        <Button onClick={openBuilder} data-testid="feedback-new-btn"><MessageSquarePlus className="w-4 h-4 mr-2" />New form</Button>
      </div>

      {loading ? (
        <div className="p-8 text-center text-sm text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</div>
      ) : visibleForms.length === 0 ? (
        <Card className="p-10 text-center text-muted-foreground" data-testid="feedback-empty">No feedback forms in this scope. Create your first one.</Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {visibleForms.map(f => (
            <Card key={f.form_id} className="p-4 space-y-3" data-testid={`feedback-card-${f.form_id}`}>
              <div className="flex justify-between items-start gap-2">
                <div className="min-w-0">
                  <div className="font-display font-semibold truncate">{f.title}</div>
                  {f.description && <div className="text-xs text-muted-foreground truncate">{f.description}</div>}
                </div>
                <Badge variant="secondary">{f.response_count || 0} responses</Badge>
              </div>
              <div className="text-xs text-muted-foreground">{(f.questions || []).length} questions · {f.anonymous ? 'Anonymous' : 'Named'}</div>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={() => copyLink(f)} data-testid={`feedback-copy-${f.form_id}`}><Link2 className="w-3.5 h-3.5 mr-1" />Copy link</Button>
                <Button size="sm" variant="outline" onClick={() => openResults(f)} data-testid={`feedback-results-${f.form_id}`}><BarChart3 className="w-3.5 h-3.5 mr-1" />Results</Button>
                <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => remove(f)} data-testid={`feedback-delete-${f.form_id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Builder dialog */}
      <Dialog open={builderOpen} onOpenChange={setBuilderOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="feedback-builder">
          <DialogHeader><DialogTitle>New feedback form</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Title</Label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Batch 9 — Training feedback" data-testid="feedback-title-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Description (optional)</Label>
              <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} />
            </div>
            <div className="flex gap-4">
              <div className="space-y-1.5 flex-1">
                <Label>Batch</Label>
                <Select value={batchId} onValueChange={setBatchId}>
                  <SelectTrigger data-testid="feedback-batch-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL}>No specific batch</SelectItem>
                    {batches.map(b => <SelectItem key={b.batch_id} value={b.batch_id}>{b.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2 pt-7">
                <Switch checked={anonymous} onCheckedChange={setAnonymous} data-testid="feedback-anon-switch" />
                <Label>Anonymous responses</Label>
              </div>
            </div>

            <div className="space-y-3">
              <Label>Questions</Label>
              {questions.map((q, i) => (
                <div key={q.id} className="rounded-lg border p-3 space-y-2" data-testid={`feedback-q-${i}`}>
                  <div className="flex gap-2 items-center">
                    <Select value={q.type} onValueChange={(v) => updateQ(q.id, { type: v, options: v === 'choice' ? (q.options.length ? q.options : ['', '']) : [] })}>
                      <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
                      <SelectContent>{QTYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                    </Select>
                    <Input className="flex-1" value={q.prompt} onChange={(e) => updateQ(q.id, { prompt: e.target.value })} placeholder="Question prompt" data-testid={`feedback-q-prompt-${i}`} />
                    <div className="flex items-center gap-1 text-xs"><Switch checked={q.required} onCheckedChange={(v) => updateQ(q.id, { required: v })} /> Req</div>
                    <Button size="icon" variant="ghost" onClick={() => removeQ(q.id)}><X className="w-4 h-4" /></Button>
                  </div>
                  {q.type === 'choice' && (
                    <div className="space-y-1.5 pl-1">
                      {q.options.map((opt, oi) => (
                        <div key={oi} className="flex gap-2">
                          <Input value={opt} onChange={(e) => updateQ(q.id, { options: q.options.map((o, k) => k === oi ? e.target.value : o) })} placeholder={`Option ${oi + 1}`} />
                          <Button size="icon" variant="ghost" onClick={() => updateQ(q.id, { options: q.options.filter((_, k) => k !== oi) })}><X className="w-4 h-4" /></Button>
                        </div>
                      ))}
                      <Button size="sm" variant="outline" onClick={() => updateQ(q.id, { options: [...q.options, ''] })}><Plus className="w-3 h-3 mr-1" />Option</Button>
                    </div>
                  )}
                </div>
              ))}
              <div className="flex flex-wrap gap-2">
                {QTYPES.map(t => <Button key={t.value} size="sm" variant="outline" onClick={() => addQ(t.value)} data-testid={`feedback-add-${t.value}`}><Plus className="w-3 h-3 mr-1" />{t.label}</Button>)}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBuilderOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="feedback-save-btn">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create form'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Results dialog */}
      <Dialog open={!!results} onOpenChange={(o) => !o && setResults(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="feedback-results-dialog">
          <DialogHeader><DialogTitle>{results?.form?.title} · {results?.total_responses} responses</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {(results?.summaries || []).map(s => (
              <div key={s.id} className="rounded-lg border p-3">
                <div className="font-medium text-sm mb-1">{s.prompt}</div>
                {(s.type === 'rating' || s.type === 'nps') && (
                  <div className="flex items-center gap-2 text-amber-600 font-display text-lg font-bold">
                    {s.type === 'rating' && <Star className="w-4 h-4 fill-amber-400 text-amber-400" />}
                    {s.average ?? '—'}{s.type === 'nps' && s.nps !== undefined && <span className="text-sm text-muted-foreground font-normal">· NPS {s.nps}</span>}
                  </div>
                )}
                {s.type === 'choice' && (
                  <div className="space-y-1">
                    {(s.distribution || []).map(d => <div key={d.option} className="text-sm flex justify-between"><span>{d.option}</span><span className="text-muted-foreground">{d.count}</span></div>)}
                  </div>
                )}
                {s.type === 'text' && (
                  <div className="space-y-1 max-h-40 overflow-y-auto">
                    {(s.responses || []).length === 0 ? <span className="text-sm text-muted-foreground">No responses</span>
                      : s.responses.map((r, i) => <div key={i} className="text-sm border-l-2 border-muted pl-2">{r}</div>)}
                  </div>
                )}
                <div className="text-xs text-muted-foreground mt-1">{s.answered} answered</div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
